// frontend/src/pages/UserManagement.js

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Avatar,
  InputAdornment,
  Pagination,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Person,
  Email,
  AdminPanelSettings,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import usePageTitle from '../hooks/usePageTitle';

// Define available pages
const AVAILABLE_PAGES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'tv-dashboard', label: 'TV Dashboard' },
  { key: 'manufacturing', label: 'Manufacturing' },
  { key: 'shop-floor', label: 'Shop Floor' },
  { key: 'inventory', label: 'Inventory Management' },
  { key: 'work-orders', label: 'Work Orders' },
  { key: 'customer-orders', label: 'Customer Orders' },
  { key: 'customers', label: 'Customers' },  
  { key: 'bom', label: 'Bill of Materials' },
  { key: 'bom-swap', label: 'BOM Component Swap' },
  { key: 'items', label: 'Items Management' },
  { key: 'operations', label: 'Operations' },
  { key: 'convert', label: 'Stock Conversion' },
  { key: 'dispatch', label: 'Dispatch Management' },
  { key: 'sage-import', label: 'Sage Import' },
  { key: 'sage-mappings', label: 'Code Mappings' },
  { key: 'import', label: 'Data Import' },
  { key: 'settings', label: 'Settings' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'reports', label: 'Reports' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'profile', label: 'Profile' },
  { key: 'create-work-order', label: 'Create Work Order' },
  { key: 'capacity-planning', label: 'Capacity Planning' },
  { key: 'stock-procurement', label: 'Stock Procurement' },
  { key: 'work-orders-v2', label: 'Work Orders V2 (Beta)' },
  { key: 'shop-floor-v2', label: 'Shop Floor V2 (Beta)' },
  { key: 'work-orders-v2-create', label: 'Create Work Order V2 (Beta)' },
  { key: 'transaction-history-v2', label: 'Transaction History V2 (Beta)' },
  { key: 'capacity-planning-v2', label: 'Capacity Planning V2 (Beta)' },
  { key: 'manufacturing-dashboard-v2', label: 'Manufacturing Dashboard V2 (Beta)' },
  { key: 'kpi-dashboard-v2', label: 'KPI Dashboard V2 (Beta)' },
  { key: 'wo-creation-log', label: 'WO Creation Log' },
  { key: 'material-shortages-v2', label: 'Material Shortages V2' },
  { key: 'scrap-v2', label: 'Scrap V2' },
];

// Feature-level permissions (stored alongside page keys in allowed_pages).
// Admins always have these implicitly. `page` is the AVAILABLE_PAGES key the
// permission fine-tunes — the dialogs group permissions under that page.
const FEATURE_PERMISSIONS = [
  {
    key: 'edit-dispatch-notes',
    page: 'dispatch',
    label: 'Edit Dispatch Notes',
    description: 'Can type in the dispatch notes box on the Dispatch page',
  },
  {
    key: 'change-order-status',
    page: 'customer-orders',
    label: 'Change Order Status',
    description: 'Can use the order status dropdown on the Customer Order detail page',
  },
];

// Per-page feature permission checkboxes, grouped by the page each permission
// belongs to. Shared by the create and edit user dialogs.
const FeaturePermissionsSection = ({ formData, onToggle }) => (
  <Grid item xs={12}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Per-Page Feature Permissions</Typography>
    {[...new Set(FEATURE_PERMISSIONS.map(p => p.page))].map(pageKey => (
      <Box key={pageKey} sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
          {AVAILABLE_PAGES.find(p => p.key === pageKey)?.label || pageKey}
        </Typography>
        {FEATURE_PERMISSIONS.filter(p => p.page === pageKey).map(perm => (
          <FormControlLabel
            key={perm.key}
            sx={{ display: 'flex', ml: 1 }}
            control={
              <Checkbox
                checked={formData.is_staff || formData.allowed_pages.includes(perm.key)}
                disabled={formData.is_staff}
                onChange={(e) => onToggle(perm.key, e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{perm.label}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {perm.description}
                </Typography>
              </Box>
            }
          />
        ))}
      </Box>
    ))}
    {formData.is_staff && (
      <Typography variant="caption" color="text.secondary" display="block">
        Administrators always have all feature permissions
      </Typography>
    )}
  </Grid>
);

const UserManagement = () => {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    is_staff: false,
    is_active: true,
    role: 'viewer',
    department: '',
    phone: '',
    allowed_pages: []
  });
  const [formErrors, setFormErrors] = useState({});

  usePageTitle('User Management');

  useEffect(() => {
    fetchUsers();
  }, [page, searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        ...(searchTerm && { search: searchTerm })
      });
      
      const response = await api.get(`/api/admin/users/?${params}`);
      setUsers(response.data.users);
      setTotalPages(response.data.pagination.total_pages);
      setTotalCount(response.data.pagination.total_count);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setFormErrors({});
      const response = await api.post('/api/admin/users/create/', formData);
      
      if (response.data.success) {
        toast.success('User created successfully!');
        setCreateDialogOpen(false);
        resetForm();
        fetchUsers();
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setFormErrors({ general: error.response.data.error });
      } else {
        toast.error('Failed to create user');
      }
    }
  };

  const handleUpdateUser = async () => {
    try {
      setFormErrors({});
      const response = await api.put(`/api/admin/users/${selectedUser.id}/`, formData);
      
      if (response.data.success) {
        toast.success('User updated successfully!');
        setEditDialogOpen(false);
        resetForm();
        fetchUsers();
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setFormErrors({ general: error.response.data.error });
      } else {
        toast.error('Failed to update user');
      }
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/api/admin/users/${selectedUser.id}/`);
      toast.success('User deleted successfully!');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to delete user');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      is_staff: false,
      is_active: true,
      role: 'viewer',
      department: '',
      phone: '',
      allowed_pages: []
    });
    setFormErrors({});
    setSelectedUser(null);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      is_staff: user.is_staff,
      is_active: user.is_active,
      role: user.profile?.role || 'viewer',
      department: user.profile?.department || '',
      phone: user.profile?.phone || '',
      allowed_pages: user.profile?.allowed_pages || []
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleFeaturePermission = (key, checked) => {
    const next = checked
      ? [...new Set([...formData.allowed_pages, key])]
      : formData.allowed_pages.filter(k => k !== key);
    handleFormChange('allowed_pages', next);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={600}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ borderRadius: 2 }}
        >
          Add User
        </Button>
      </Box>

      {/* Search and Stats */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Chip 
                label={`Total: ${totalCount}`} 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label={`Active: ${users.filter(u => u.is_active).length}`} 
                color="success" 
                variant="outlined" 
              />
              <Chip 
                label={`Admins: ${users.filter(u => u.is_staff).length}`} 
                color="warning" 
                variant="outlined" 
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: user.is_staff ? 'error.main' : 'primary.main' }}>
                          {user.username[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email || 'No email'}
                          </Typography>
                          {user.first_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {user.first_name} {user.last_name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.is_staff ? <AdminPanelSettings /> : <Person />}
                        label={user.is_staff ? 'Admin' : (user.profile?.role || 'User')}
                        color={user.is_staff ? 'error' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.profile?.department || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.is_active ? <CheckCircle /> : <Cancel />}
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.date_joined}</TableCell>
                    <TableCell>{user.last_login}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(user)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(user)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username *"
                value={formData.username}
                onChange={(e) => handleFormChange('username', e.target.value)}
                error={!!formErrors.username}
                helperText={formErrors.username}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password *"
                type="password"
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password || "Minimum 6 characters"}
              />
            </Grid>
            
            {/* NEW: Role Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  label="Role"
                  disabled={formData.is_staff}
                >
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="operator">Operator</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* NEW: Department */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => handleFormChange('department', e.target.value)}
                placeholder="e.g., Production, Quality Control"
              />
            </Grid>

            {/* NEW: Phone */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                placeholder="e.g., +44 20 1234 5678"
              />
            </Grid>

            {/* NEW: Page Permissions */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Allowed Pages</InputLabel>
                <Select
                  multiple
                  value={formData.allowed_pages}
                  onChange={(e) => handleFormChange('allowed_pages', e.target.value)}
                  input={<OutlinedInput label="Allowed Pages" />}
                  renderValue={(selected) =>
                    selected
                      .map(key => AVAILABLE_PAGES.find(p => p.key === key)?.label)
                      .filter(Boolean)
                      .join(', ')
                  }
                  disabled={formData.is_staff}
                >
                  {AVAILABLE_PAGES.map((page) => (
                    <MenuItem key={page.key} value={page.key}>
                      <Checkbox checked={formData.allowed_pages.indexOf(page.key) > -1} />
                      <ListItemText primary={page.label} />
                    </MenuItem>
                  ))}
                </Select>
                {formData.is_staff && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Administrators have access to all pages
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Feature Permissions */}
            <FeaturePermissionsSection formData={formData} onToggle={toggleFeaturePermission} />

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_staff}
                    onChange={(e) => {
                      handleFormChange('is_staff', e.target.checked);
                      // Clear allowed_pages when making admin
                      if (e.target.checked) {
                        handleFormChange('allowed_pages', []);
                      }
                    }}
                  />
                }
                label="Administrator privileges (access to all pages)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  />
                }
                label="Active user"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
            Cancel
          </Button>
          <Button onClick={handleCreateUser} variant="contained">
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                disabled
                helperText="Username cannot be changed"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                helperText="Leave blank to keep current password"
              />
            </Grid>

            {/* Role Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  label="Role"
                  disabled={formData.is_staff}
                >
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="operator">Operator</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Department */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => handleFormChange('department', e.target.value)}
                placeholder="e.g., Production, Quality Control"
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                placeholder="e.g., +44 20 1234 5678"
              />
            </Grid>

            {/* Page Permissions */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Allowed Pages</InputLabel>
                <Select
                  multiple
                  value={formData.allowed_pages}
                  onChange={(e) => handleFormChange('allowed_pages', e.target.value)}
                  input={<OutlinedInput label="Allowed Pages" />}
                  renderValue={(selected) =>
                    selected
                      .map(key => AVAILABLE_PAGES.find(p => p.key === key)?.label)
                      .filter(Boolean)
                      .join(', ')
                  }
                  disabled={formData.is_staff}
                >
                  {AVAILABLE_PAGES.map((page) => (
                    <MenuItem key={page.key} value={page.key}>
                      <Checkbox checked={formData.allowed_pages.indexOf(page.key) > -1} />
                      <ListItemText primary={page.label} />
                    </MenuItem>
                  ))}
                </Select>
                {formData.is_staff && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Administrators have access to all pages
                  </Typography>
                )}
                {!formData.is_staff && formData.allowed_pages.length > 0 && (
                  <Typography variant="caption" color="primary" sx={{ mt: 1 }}>
                    {formData.allowed_pages.length} page{formData.allowed_pages.length !== 1 ? 's' : ''} selected
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Feature Permissions */}
            <FeaturePermissionsSection formData={formData} onToggle={toggleFeaturePermission} />

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_staff}
                    onChange={(e) => {
                      handleFormChange('is_staff', e.target.checked);
                      // Clear allowed_pages when making admin
                      if (e.target.checked) {
                        handleFormChange('allowed_pages', []);
                      }
                    }}
                  />
                }
                label="Administrator privileges (access to all pages)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  />
                }
                label="Active user"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); resetForm(); }}>
            Cancel
          </Button>
          <Button onClick={handleUpdateUser} variant="contained">
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.username}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;